import { Head, Link } from '@inertiajs/react';

import {
    DesignFooter,
    DesignHeader,
    UtilityBar,
} from '@/Components/Design';

type Resource = {
    slug: string;
    title: string;
    excerpt: string | null;
    category: string | null;
    publishedAt: string | null;
};

type Props = { resources: Resource[] };

export default function ResourcesIndex({ resources }: Props) {
    return (
        <>
            <Head>
                <title>Resources — Personal injury guides and explainers</title>
            </Head>
            <div style={{ background: 'var(--paper)', minHeight: '100vh' }}>
                <UtilityBar />
                <DesignHeader active="Resources" />

                <section data-res-section="hero" style={{ maxWidth: 1200, margin: '0 auto', padding: '64px 56px 40px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--ink-mute)', marginBottom: 24 }}>
                        <Link href="/" style={{ color: 'inherit', textDecoration: 'none' }}>Home</Link>
                        <span>/</span>
                        <span style={{ color: 'var(--ink)' }}>Resources</span>
                    </div>
                    <div style={{ fontSize: 12, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--ember)', marginBottom: 10, fontWeight: 600 }}>Resources</div>
                    <h1 style={{ fontSize: 64, lineHeight: 1.05, letterSpacing: '-0.025em', marginBottom: 16, fontFamily: 'var(--font-serif)', fontWeight: 500 }}>
                        Personal injury guides &amp; explainers
                    </h1>
                    <p style={{ fontSize: 18, color: 'var(--ink-soft)', lineHeight: 1.55, maxWidth: 620 }}>
                        Plain-English writing on what happens after an accident, how no-win-no-fee actually works, statute-of-limitations basics, and how to evaluate an attorney.
                    </p>
                </section>

                <section data-res-section="list" style={{ maxWidth: 1200, margin: '0 auto', padding: '0 56px 80px' }}>
                    {resources.length === 0 ? (
                        <div style={{ background: 'var(--card)', border: '1px solid var(--rule)', borderRadius: 12, padding: 40, textAlign: 'center' }}>
                            <div style={{ fontFamily: 'var(--font-serif)', fontSize: 22, fontWeight: 500, marginBottom: 8 }}>No resources published yet.</div>
                            <p style={{ fontSize: 14, color: 'var(--ink-soft)' }}>Editorial team is reviewing. Check back next week.</p>
                        </div>
                    ) : (
                        <div data-res-grid style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
                            {resources.map((r) => (
                                <Link
                                    key={r.slug}
                                    href={`/resources/${r.slug}`}
                                    style={{
                                        background: 'var(--card)',
                                        border: '1px solid var(--rule)',
                                        borderRadius: 14,
                                        padding: 28,
                                        textDecoration: 'none',
                                        color: 'inherit',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 12,
                                        transition: 'all var(--dur) var(--ease)',
                                    }}
                                >
                                    {r.category && (
                                        <div style={{ fontSize: 11, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--ember)', fontWeight: 600 }}>
                                            {r.category}
                                        </div>
                                    )}
                                    <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: 22, fontWeight: 500, lineHeight: 1.2, letterSpacing: '-0.01em' }}>
                                        {r.title}
                                    </h2>
                                    {r.excerpt && (
                                        <p style={{ fontSize: 14, color: 'var(--ink-soft)', lineHeight: 1.55 }}>{r.excerpt}</p>
                                    )}
                                    {r.publishedAt && (
                                        <div style={{ marginTop: 'auto', paddingTop: 12, fontSize: 12, color: 'var(--ink-mute)' }}>
                                            {new Date(r.publishedAt).toLocaleDateString()}
                                        </div>
                                    )}
                                </Link>
                            ))}
                        </div>
                    )}
                </section>

                <DesignFooter />
            </div>
        </>
    );
}
